import { useState } from "react";
import {
  useSearchBarChartDataQuery,
  useSearchPieChartDataQuery,
} from "../../dashboard/dashBoardApi";
import { Col, Row } from "react-bootstrap";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import useTranslator from "../../../hooks/useTranslator";

export const ReceivingCharts = () => {
  const { _t } = useTranslator();

  const [pieFilter, setPieFilter] = useState("monthly");
  const [barFilter, setBarFilter] = useState("monthly");

  const filterMap = {
    [_t("today", "Today")]: "hourly",
    [_t("weekly", "Weekly")]: "weekly",
    [_t("monthly", "Monthly")]: "monthly",
  };

  const { data: barChartData } = useSearchBarChartDataQuery({
    filter: barFilter,
  });
  const { data: pieChartData } = useSearchPieChartDataQuery({
    filter: pieFilter,
  });

  const hasBarData =
    Array.isArray(barChartData?.data?.series) &&
    barChartData.data.series.length > 0;
  const hasPieData =
    Array.isArray(pieChartData?.data?.data) &&
    pieChartData.data.data.length > 0;

  const noDataLabel = _t("no_data", "No Data");
  const totalCasesLabel = _t("total_cases", "Total Cases");
  const casesLabel = _t("cases", "Cases");

  const options = {
    chart: { type: "column" },
    title: { text: null, align: "left" },
    xAxis: {
      categories: hasBarData ? barChartData.data.categories : [noDataLabel],
      labels: { style: { fontSize: "12px", color: "#8C97A7" } },
    },
    yAxis: { title: { text: casesLabel } },
    legend: { align: "center", verticalAlign: "top", layout: "horizontal" },
    tooltip: { shared: true },
    plotOptions: {
      column: {
        grouping: true,
        borderWidth: 0,
        dataLabels: { enabled: false },
      },
    },
    series: hasBarData
      ? barChartData.data.series.map((series) => ({
          name: _t(series?.name, series?.name) || "",
          data: Array.isArray(series?.data) ? [...series.data] : [],
          color:
            series?.name === "Total Cases"
              ? "#f5a623"
              : series?.name === "Accepted Cases"
              ? "#28a745"
              : "#dc3545",
        }))
      : [{ name: noDataLabel, data: [0], color: "#E8E8E8" }],
  };

  const totalCases =
    pieChartData?.data?.data.find((item) => item.name === "Total Cases")?.y ||
    0;

  const optionsDonut = {
    chart: { type: "pie" },
    title: {
      text: hasPieData
        ? `<p>${totalCasesLabel}</p><br />${totalCases}`
        : `<p>${noDataLabel}</p>`,
      verticalAlign: "middle",
      y: -20,
    },
    tooltip: { pointFormat: "{series.name}: <b>{point.y}</b>" },
    plotOptions: {
      pie: {
        innerSize: "70%",
        dataLabels: { enabled: false },
        showInLegend: true,
      },
    },
    legend: {
      layout: "vertical",
      align: "center",
      verticalAlign: "bottom",
      itemMarginBottom: 5,
      itemStyle: { color: "#8C97A7", fontSize: "14px" },
    },
    series: [
      {
        name: casesLabel,
        data: hasPieData
          ? pieChartData.data.data
              .filter((series) => series.name !== "Total Cases")
              .map((series) => ({
                name: _t(series.name, series.name),
                y: series.y,
                color:
                  series.name === "Accepted Cases"
                    ? "#46C389"
                    : series.name === "Rejected Cases"
                    ? "#dc3545"
                    : "#54C9C9",
              }))
          : [{ name: noDataLabel, y: 100, color: "#E8E8E8" }],
      },
    ],
  };

  return (
    <Row className="mb-3">
      <Col lg={7}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">
              {_t("supplier_data_overview", "Supplier Data Overview")}
            </h6>
            <ul className="chart_filter">
              {Object.keys(filterMap).map((label, index) => (
                <li
                  key={index}
                  onClick={() => setBarFilter(filterMap[label])}
                  className={filterMap[label] === barFilter ? "active" : ""}
                >
                  {label}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2">
            <HighchartsReact highcharts={Highcharts} options={options} />
          </div>
        </div>
      </Col>
      <Col lg={5}>
        <div className="cmn_card">
          <div className="overview_header d-flex gap-2 align-items-center">
            <h6 className="mb-0 flex-grow-1">
              {_t("washing_data", "Washing Data")}
            </h6>
            <ul className="chart_filter">
              {Object.keys(filterMap).map((label, index) => (
                <li
                  key={index}
                  onClick={() => setPieFilter(filterMap[label])}
                  className={filterMap[label] === pieFilter ? "active" : ""}
                >
                  {label}
                </li>
              ))}
            </ul>
          </div>
          <div className="p-2 position-relative">
            <HighchartsReact highcharts={Highcharts} options={optionsDonut} />
          </div>
        </div>
      </Col>
    </Row>
  );
};
